import React, { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart3,
  TrendingUp,
  FileText,
  Calculator,
  ArrowLeft,
  Moon,
  Sun,
  LogOut,
  Shield,
  UserCog,
  FileSpreadsheet,
  FolderOpen,
  Landmark,
  UserCheck
} from 'lucide-react';

const CompanyApps = ({ user, onLogout, onBack, onAdminPanel }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const applications = [
    {
      id: 'situatii-lucrari',
      name: 'Situații Lucrări',
      description: 'Monitorizare situații de lucrări',
      icon: FileText,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      url: 'https://nxtstp.ro/situatii-lucrari/login'
    },
    {
      id: 'ofertare',
      name: 'Ofertare',
      description: 'Creare și gestiune oferte',
      icon: BarChart3,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      url: 'https://nxtstp.ro/ofertare/login'
    },
    {
      id: 'cash-flow',
      name: 'Cashflow App',
      description: 'Analiză și management flux de numerar',
      icon: TrendingUp,
      iconBg: 'bg-teal-100 dark:bg-teal-900/30',
      iconColor: 'text-teal-600 dark:text-teal-400'
    },
    {
      id: 'payslip-distributor',
      name: 'Payslip Distributor',
      description: 'Distribuire fluturaș de salariu',
      icon: FileSpreadsheet,
      iconBg: 'bg-pink-100 dark:bg-pink-900/30',
      iconColor: 'text-pink-600 dark:text-pink-400',
      url: 'https://nxtstp.ro/payslip-distributor/login'
    },
    {
      id: 'dosar-angajati',
      name: 'Dosar angajați',
      description: 'Gestionare dosare și documente angajați',
      icon: FolderOpen,
      iconBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400',
      url: 'https://nxtstp.ro/dosar-angajati/login'
    },
    {
      id: 'payslip-calculator',
      name: 'PaySlip Calculator',
      description: 'Calcul și generare fluturași de salariu',
      icon: Calculator,
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600 dark:text-orange-400',
      url: 'https://nxtstp.ro/payslip-calculator'
    },
    {
      id: 'buget-app',
      name: 'Buget App',
      description: 'Gestionare și monitorizare buget',
      icon: BarChart3,
      iconBg: 'bg-cyan-100 dark:bg-cyan-900/30',
      iconColor: 'text-cyan-600 dark:text-cyan-400',
      url: 'https://nxtstp.ro/buget-app'
    },
    {
      id: 'divan',
      name: 'Divan',
      description: 'Aplicație exclusivă pentru administrator',
      icon: Landmark,
      iconBg: 'bg-rose-100 dark:bg-rose-900/30',
      iconColor: 'text-rose-600 dark:text-rose-400',
      adminOnly: true,
      url: 'https://nxtstp.ro/divan'
    },
    {
      id: '007-agent-de-lucru',
      name: '007 - Agent de lucru',
      description: 'Aplicație exclusivă pentru administrator',
      icon: UserCheck,
      iconBg: 'bg-slate-100 dark:bg-slate-900/30',
      iconColor: 'text-slate-600 dark:text-slate-400',
      adminOnly: true,
      url: 'https://nxtstp.ro/007-agent-de-lucru'
    }
  ];

  // Filter applications based on user permissions
  // Supreme admin and admin see all apps
  // Admin-only apps are visible only to supreme_admin
  const filteredApplications = applications.filter(app => {
    if (app.adminOnly) {
      return user?.role === 'supreme_admin';
    }
    if (user?.role === 'supreme_admin' || user?.role === 'admin') {
      return true;
    }
    return user?.app_permissions?.includes(app.id);
  });

  // Track opened windows to avoid duplicate tabs
  const windowRefs = useRef({});

  const handleAppClick = (appId) => {
    const app = applications.find(a => a.id === appId);
    if (!app) return;

    // If app has a URL, open/focus it
    if (app.url) {
      const existing = windowRefs.current[app.id];
      if (existing && !existing.closed) {
        existing.focus();
        return;
      }
      windowRefs.current[app.id] = window.open(app.url, `app-${app.id}`);
      return;
    }

    // Placeholder for apps without URLs
    setSelectedApp(appId);
    setTimeout(() => {
      window.open('/' + appId, '_blank');
      setSelectedApp(null);
    }, 300);
  };

  const isAdmin = user && (user.role === 'supreme_admin' || user.role === 'admin');

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-elegant-md">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Company Area</h1>
              <p className="text-xs text-muted-foreground">
                Bine ai venit, {user?.full_name} {user?.role === 'supreme_admin' && '👑'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                size="icon"
                onClick={onAdminPanel}
                className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
                title="Panou Administrator"
              >
                <UserCog className="h-5 w-5" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleDarkMode}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={onLogout}
              className="rounded-xl border-border hover:bg-destructive hover:text-destructive-foreground transition-smooth"
              title="Deconectare"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Company Area
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Acces complet la aplicațiile companiei. Selectează pentru a începe.
            </p>
          </div>

          {/* Applications Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-scale-in">
            {filteredApplications.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-muted-foreground text-lg">
                  Nu ai permisiuni pentru nicio aplicație. Contactează administratorul.
                </p>
              </div>
            ) : (
              filteredApplications.map((app, index) => {
              const Icon = app.icon;
              const isSelected = selectedApp === app.id;
              
              return (
                <Card
                  key={app.id}
                  data-testid={`company-app-${app.id}`}
                  className={`group relative bg-card border-border hover:border-accent/50 rounded-2xl p-6 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-elegant-lg hover:-translate-y-1 ${
                    isSelected ? 'scale-95 opacity-50' : ''
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => handleAppClick(app.id)}
                >
                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/0 via-accent/0 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`${app.iconBg} w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-7 h-7 ${app.iconColor}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-accent transition-colors duration-300">
                        {app.name}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {app.description}
                      </p>
                    </div>
                    
                    {/* Arrow indicator */}
                    <div className="mt-4 flex items-center text-accent text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
                      <span>Lansează</span>
                      <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Card>
              );
            })
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CompanyApps;
